<template>
  <div
    :class="classNames"
    :style="{ 'max-height': `${maxHeight}px` }"
    tabindex="-1"
  >
    <div
      v-if="isShowHeader"
      ref="dialogHeaderRef"
      class="kk-dialog__header"
      :class="{
        'kk-dialog__header--transparent': headerTransparent,
      }"
    >
      <slot name="header">
        <KkIconButton
          v-if="isShowBack"
          :class="['kk-dialog__left', ...iconClassNames]"
          :is-transparent="true"
          icon-name="ic_arrowLeft_line_semibold"
          @click="$emit('back')"
        />

        <div
          class="kk-dialog__title"
          :class="[
            `kk-dialog__title--h${titleFont}`,
            titleLineClamp && `kk-dialog__title--${titleLineClamp}`,
          ]"
        >
          {{ title }}
        </div>

        <div
          v-if="extraIcon || isShowClose"
          class="kk-dialog__right"
        >
          <KkIconButton
            v-if="extraIcon"
            :class="iconClassNames"
            :is-transparent="true"
            :icon-name="extraIcon"
            @click="$emit('extra-icon')"
          />

          <KkIconButton
            v-if="isShowClose"
            :class="iconClassNames"
            :is-transparent="true"
            icon-name="ic_cross_line_semibold_lg"
            @click="$emit('close')"
          />
        </div>
      </slot>
    </div>

    <div
      ref="dialogBodyRef"
      class="kk-dialog__body"
      :class="bodyClass"
    >
      <slot />
    </div>

    <div
      v-if="isShowFooter"
      class="kk-dialog__footer"
      :class="{ 'kk-dialog__footer--shadow': showBoxShadow }"
    >
      <slot name="footer">
        <KkDialogButtonGroup
          :footer-direction="footerDirection"
          :footer-align="footerAlign"
          :footer-options="footerOptions"
        />
      </slot>
    </div>
  </div>
</template>

<script setup>
import {
  nextTick,
  ref,
  computed,
  onMounted,
  onUnmounted,
  defineExpose,
} from 'vue';
import { isNumber, isElement } from 'lodash-es';
import KkIconButton from '../../KkIconButton';
import { dialogContentProps } from './dialog';
import KkDialogButtonGroup from './KkDialogButtonGroup.vue';

const props = defineProps(dialogContentProps);

const dialogHeaderRef = ref(null);
const dialogBodyRef = ref(null);
const isScrollable = ref(false);
const isScrollBottom = ref(false);

const classNames = computed(() => [
  'kk-dialog',
  `kk-dialog--${props.size}`,
  props.isFullScreen && 'kk-dialog-fullscreen',
]);
const iconClassNames = computed(() => [
  'kk-dialog__icon',
  props.headerTransparent && 'transparent',
]);

const showBoxShadow = computed(() => {
  return isScrollable.value && !isScrollBottom.value && props.isShowBoxShadow;
});
const targetHeight = computed(() => {
  if (isNumber(props.scrollTarget)) {
    return props.scrollTarget;
  } else if (isElement(props.scrollTarget)) {
    return props.scrollTarget.clientHeight;
  } else if (props.scrollTarget?.$el) {
    return props.scrollTarget.$el.clientHeight;
  }

  return 0;
});

onMounted(async () => {
  await nextTick();

  const element = dialogBodyRef.value;

  // `element.offsetParent` to check if element is visible before performing calculations
  if (element && element.offsetParent !== null) {
    const { scrollHeight, clientHeight } = element;
    isScrollable.value = scrollHeight > clientHeight;

    element.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  const element = dialogBodyRef.value;
  if (element) {
    element.addEventListener('scroll', handleScroll);
  }
});
// check if the content is scrolled to the bottom
function checkScrollBottom() {
  const { scrollHeight, scrollTop, clientHeight } = dialogBodyRef.value;
  // when calculating scrollHeight, there may be precision loss or rounding errors
  isScrollBottom.value = scrollTop + clientHeight >= scrollHeight - 1;
}
// change the header background transparency based on scroll position
function setHeaderBackground() {
  const { scrollTop } = dialogBodyRef.value;
  const opacity = (scrollTop / targetHeight.value).toFixed(1);

  dialogHeaderRef.value.style.backgroundColor = `rgba(255, 255, 255, ${opacity})`;
}

function handleScroll() {
  checkScrollBottom();

  if (props.isShowHeader) {
    setHeaderBackground();
  }
}

defineExpose({
  dialogBodyRef,
});
</script>
